import type { ComponentProps, ReactElement } from 'react'
import { forwardRef, useCallback, useMemo, useRef, useImperativeHandle } from 'react'
import { v4 as uuidv4 } from 'uuid'
import _SearchTileBase from './_SearchTileBase'
import _SearchTileContent from './_SearchTileContent'
import _SearchTileContentPrimary from './_SearchTileContentPrimary'
import _SearchTileContentSecondary from './_SearchTileContentSecondary'
import _SearchTileHero from './_SearchTileHero'
import _SearchTilePricing from './_SearchTilePricing'
import type { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import type { SearchTileLogo } from './SearchTile.types'
import useHotelSearchPriceAlignment from 'hooks/Experiments/useHotelSearchPriceAlignment'
import { isBedbankOffer, isBundleOffer, isLEHotel, isVillaOffer } from 'lib/offer/offerTypes'
import SearchTilePricingColumnContext from './SearchTilePricingColumnContext'
import useSearchTilePricingColumn from './useSearchTilePricingColumn'

export type { SearchTileLogo } from './SearchTile.types'

type Inherited_SearchTileBaseProps = Pick<ComponentProps<typeof _SearchTileBase>, 'state' | 'background' | 'banner' | 'density' | 'treatment'>
type InheritedSearchTileContentProps = Pick<ComponentProps<typeof _SearchTileContent>, 'bottomExtension' | 'topExtension' | 'density'>
type InheritedSearchTileHeroProps = Pick<ComponentProps<typeof _SearchTileHero>, 'bookmarkAction' | 'eagerLoadFirstImage' | 'images' | 'onImageChange' | 'onImageLoad' | 'productLabel' | 'showImageMosaic'>
type InheritedSearchTilePricingProps = Pick<ComponentProps<typeof _SearchTilePricing>, 'action'>
type InheritedSearchTileContentPrimaryProps = Pick<ComponentProps<typeof _SearchTileContentPrimary>, 'cancellationPolicyLabel' | 'features' | 'labels' | 'logo' | 'meta' | 'overline' | 'rating' | 'sponsored' | 'subtitle' | 'title' | 'titleLineClamp'>

interface Props extends Inherited_SearchTileBaseProps, InheritedSearchTileContentProps, InheritedSearchTileContentPrimaryProps, InheritedSearchTileHeroProps, InheritedSearchTilePricingProps, Utils.PropsWithDataAttributes {
  logo?: SearchTileLogo
  /**
   * Price point rows and stacks
   */
  pricePoint?: ReactElement | false
  className?: string
  offer: App.AnyOffer
}

const SearchTile = forwardRef<HTMLDivElement, Props>(function SearchTile(
  {
    state,
    background,
    banner,
    density = 'default',
    treatment = 'default',
    bookmarkAction,
    eagerLoadFirstImage,
    cancellationPolicyLabel,
    features,
    images,
    labels,
    logo,
    meta,
    overline,
    productLabel,
    pricePoint,
    action,
    rating,
    sponsored,
    title,
    titleLineClamp,
    className,
    subtitle,
    bottomExtension,
    topExtension,
    onImageChange,
    onImageLoad,
    showImageMosaic = false,
    offer,
    ...dataAttributes
  },
  ref,
) {
  const { type: offerType, productType } = offer
  const listId = useMemo(() => uuidv4(), [])
  const showPricing = !!pricePoint || !!action
  const tileRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(ref, () => tileRef.current!)
  const alignHotelSearchPrices = useHotelSearchPriceAlignment()
  const fitDesktopPricing = alignHotelSearchPrices &&
    (isLEHotel(offer) || isBedbankOffer(offer) || isVillaOffer(offer) || isBundleOffer(offer))
  const hasPricingColumn = useSearchTilePricingColumn(tileRef, density, fitDesktopPricing)

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    onImageChange?.(index, image)
    if (offer && image) {
      trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'search-tile',
          listId,
          offer,
          undefined,
        ),
      )
    }
  }, [onImageChange, offer, listId])

  return <_SearchTileBase
    {...dataAttributes}
    data-product={productType}
    data-offer={offerType}
    state={state}
    background={background}
    banner={banner}
    density={density}
    treatment={treatment}
    className={className}
    ref={tileRef}
  >
    <_SearchTileHero
      bookmarkAction={bookmarkAction}
      density={density}
      eagerLoadFirstImage={eagerLoadFirstImage}
      images={images}
      productLabel={productLabel}
      showImageMosaic={showImageMosaic}
      onImageChange={handleImageChange}
      onImageLoad={onImageLoad}
    />
    <_SearchTileContent bottomExtension={bottomExtension} topExtension={topExtension} density={density} fitDesktopPricing={fitDesktopPricing}>
      <_SearchTileContentPrimary
        cancellationPolicyLabel={cancellationPolicyLabel}
        density={density}
        features={features}
        labels={labels}
        meta={meta}
        overline={overline}
        rating={rating}
        sponsored={sponsored}
        title={title}
        titleLineClamp={titleLineClamp}
        subtitle={subtitle}
        logo={logo}
      />
      <_SearchTileContentSecondary density={density} rating={rating} logo={logo}>
        {showPricing && <_SearchTilePricing action={action} density={density} fitDesktopPricing={fitDesktopPricing}>
          <SearchTilePricingColumnContext.Provider value={hasPricingColumn}>
            {pricePoint}
          </SearchTilePricingColumnContext.Provider>
        </_SearchTilePricing>}
      </_SearchTileContentSecondary>
    </_SearchTileContent>
  </_SearchTileBase>
})
export default SearchTile
